import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFullScanJob } from './full-scan-api';
import { TERMINAL_FULL_SCAN_STATUSES } from './full-scan-constants';

// If a job's status never reaches a terminal state (e.g. its SQS message
// was lost and nothing will ever process it), don't poll forever - give up
// after this long and surface it as stalled instead of silently retrying
// every 3s for as long as the report stays open.
const MAX_POLL_MS = 5 * 60 * 1000;

/**
 * Polls a full-scan job until it reaches a terminal status. Lifted out of
 * the old per-tab FullScanView so DigitalAudit.tsx can drive both the
 * top-of-page status banner and the merge-into-report effect regardless of
 * which tab is active.
 */
export const useFullScanJobPolling = (jobId: string | null) => {
  const [isStalled, setIsStalled] = useState(false);
  // Mirrors `isStalled` for refetchInterval's callback to read - that
  // callback runs outside React's render, so a ref (rather than state) is
  // the appropriate way to give it the latest value without a stale closure.
  const stalledRef = useRef(false);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const query = useQuery({
    queryKey: ['digital-audit-full-scan-job', jobId],
    queryFn: () => getFullScanJob(jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: q => {
      const status = q.state.data?.status;
      const isTerminal = Boolean(status && TERMINAL_FULL_SCAN_STATUSES.includes(status));
      return isTerminal || stalledRef.current ? false : 3000;
    }
  });

  const status = query.data?.status;
  const isTerminal = Boolean(status && TERMINAL_FULL_SCAN_STATUSES.includes(status));

  // Arms (or re-arms, on retry) the give-up timer. Pulled out of the effect
  // below so `retry` can restart it without needing `jobId`/`isTerminal` to
  // change first.
  const armStallTimer = useCallback(() => {
    clearTimeout(timeoutIdRef.current);
    timeoutIdRef.current = setTimeout(() => {
      stalledRef.current = true;
      setIsStalled(true);
    }, MAX_POLL_MS);
  }, []);

  useEffect(() => {
    stalledRef.current = false;
    setIsStalled(false);
    if (!jobId || isTerminal) {
      return undefined;
    }
    armStallTimer();
    return () => clearTimeout(timeoutIdRef.current);
  }, [jobId, isTerminal, armStallTimer]);

  // DEV-24211: a job that's stalled purely because polling gave up (not
  // because it failed) may still complete server-side later - this resumes
  // polling and gives it another MAX_POLL_MS window, instead of leaving the
  // user stuck re-running the whole scan to see results that already exist.
  const retry = () => {
    stalledRef.current = false;
    setIsStalled(false);
    armStallTimer();
    query.refetch();
  };

  return { ...query, isStalled, retry };
};
